import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, type PrismaTableColumn, usePrismaTable } from '@client/hooks/table-v2';
import { useAuthStore } from '@client/stores/AuthStore';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE, SYSTEM_ORG_IDS } from '@shared/constants';
import { hasPermission } from '@shared/permissions/roles';
import { formatDateTime } from '@shared/utils/core/formatting';
import { createFileRoute } from '@tanstack/react-router';
import { Alert, Form, InputNumber, Modal, Popconfirm, Switch, Tag, Tooltip } from 'antd';
import { Check, Play, RefreshCw, Rocket, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

export const Route = createFileRoute('/admin-console/advanced/db-upgrades')({
    staticData: {
        breadcrumb: {
            title: 'DB upgrades',
        },
        ui: {
            title: 'DB upgrades',
            subtitle: 'List of DB upgrades.',
            menuDescription: 'Manage database migrations',
        },
        permissions: ['admin_system:read'],
    },
    component: DbUpgrades,
});

type DbUpgrade = RouterOutputs['admin']['dbUpgrade']['listDbUpgrades']['rows'][number];

type QueryVariables = RouterInputs['admin']['dbUpgrade']['listDbUpgrades'];
type OrderBy = NonNullable<QueryVariables['orderBy']>;

type RetryParams = {
    useTransaction: boolean;
    txTimeout: number;
    txWait: number;
};

type UpgradeResult = RouterOutputs['admin']['dbUpgrade']['doDbUpgrade'];

function showUpgradeResult(result: UpgradeResult, refetch: () => void) {
    message.destroy();
    void refetch();

    if (result.failedUpgrades.length > 0) {
        Modal.error({
            title: 'Some upgrades failed',
            content: (
                <div className="max-h-96 overflow-auto">
                    {result.failedUpgrades.map((failure) => (
                        <div key={failure.name} className="mb-4">
                            <div className="mb-1 font-semibold">{failure.name}</div>
                            <pre className="whitespace-pre-wrap break-words rounded-sm bg-red-50 p-4 text-red-500 dark:bg-red-900/30">
                                {failure.error}
                            </pre>
                        </div>
                    ))}
                </div>
            ),
            width: 800,
        });
    } else if (result.noOfUpgradeProcessed > 0) {
        message.success(`${result.noOfUpgradeProcessed} upgrade(s) completed successfully.`);
    } else {
        message.info('No pending upgrades to run.');
    }
}

function useDbUpgradeColumns(
    canManage: boolean,
    handleRunManualMigration: (record: DbUpgrade) => void,
    setSelectedUpgrade: (upgrade: DbUpgrade | null) => void,
    setRetryModalVisible: (visible: boolean) => void,
    setUseTransaction: (useTransaction: boolean) => void,
    transactionModeByName: Map<string, boolean>,
    form: any,
    canRerun: (record: DbUpgrade) => boolean,
    canRunManually: (record: DbUpgrade) => boolean,
): readonly PrismaTableColumn<DbUpgrade, OrderBy, any>[] {
    const columnHelper = createColumnHelper<DbUpgrade, OrderBy>();

    const columns: PrismaTableColumn<DbUpgrade, OrderBy, any>[] = [
        columnHelper.accessor('id', {
            id: 'id',
            header: 'ID',
            sortable: 'id',
            cell: ({ value }) => BigInt(value).toString(),
            align: 'center',
        }),
        columnHelper.accessor('name', {
            id: 'name',
            header: 'Name',
            className: 'whitespace-nowrap',
        }),
        columnHelper.accessor('status', {
            id: 'status',
            header: 'Status',
            cell: ({ value }) => {
                const color =
                    value === 'completed'
                        ? 'success'
                        : value === 'failed'
                          ? 'error'
                          : value === 'pending'
                            ? 'warning'
                            : 'default';

                return (
                    <Tag variant="outlined" color={color}>
                        {value}
                    </Tag>
                );
            },
            className: 'whitespace-nowrap',
        }),
        columnHelper.accessor('createdAt', {
            id: 'createdAt',
            header: 'First Run At',
            cell: ({ value }) => (value ? formatDateTime(new Date(value), 'yyyy-MM-dd HH:mm:ss') : '-'),
            className: 'whitespace-nowrap',
        }),
        columnHelper.accessor('lastRunAt', {
            id: 'lastRunAt',
            header: 'Last Run At',
            cell: ({ value }) => (value ? formatDateTime(new Date(value), 'yyyy-MM-dd HH:mm:ss') : '-'),
            className: 'whitespace-nowrap',
        }),
        columnHelper.accessor('dependsOn', {
            id: 'dependsOn',
            header: 'Dependencies',
            cell: ({ value }) => value.join(', '),
        }),
        columnHelper.accessor('recordsFound', {
            id: 'recordsFound',
            header: 'Records found',
        }),
        columnHelper.accessor('recordsUpdated', {
            id: 'recordsUpdated',
            header: 'Records updated',
        }),
        columnHelper.accessor('canRerun', {
            id: 'canRerun',
            header: 'Can rerun',
            cell: ({ value }) => (value ? <Check className="text-green-500" /> : <X className="text-red-500" />),
        }),
        columnHelper.accessor('manualOnly', {
            id: 'manualOnly',
            header: 'Manual Only',
            cell: ({ value }) =>
                value ? <Check className="text-orange-500" /> : <X className="text-subtle-foreground" />,
        }),
        columnHelper.accessor('error', {
            id: 'error',
            header: 'Error',
            cell: ({ value }) =>
                value ? (
                    <div
                        className="max-w-40 cursor-pointer truncate text-ellipsis text-red-500 hover:opacity-80"
                        onClick={() => {
                            Modal.error({
                                title: 'Error Details',
                                content: (
                                    <pre className="whitespace-pre-wrap break-words rounded-sm bg-red-50 p-4 text-red-500 dark:bg-red-900/30">
                                        {value}
                                    </pre>
                                ),
                                width: 800,
                            });
                        }}
                    >
                        {value}
                    </div>
                ) : (
                    '-'
                ),
        }),
    ];

    if (canManage) {
        columns.push(
            columnHelper.actions({
                cell: ({ row }) => (
                    <div className="flex gap-2">
                        {row.manualOnly && row.status === 'pending' ? (
                            <Tooltip title="Run manual migration">
                                <Popconfirm
                                    title="Are you sure you want to run this manual migration?"
                                    onConfirm={() => handleRunManualMigration(row)}
                                    okText="Yes"
                                    cancelText="No"
                                    placement="left"
                                >
                                    <Button.Icon icon={<Rocket />} color="orange" disabled={!canRunManually(row)} />
                                </Popconfirm>
                            </Tooltip>
                        ) : (
                            <Tooltip title="Retry upgrade">
                                <Button.Icon
                                    icon={<RefreshCw />}
                                    color="blue"
                                    disabled={!canRerun(row)}
                                    onClick={() => {
                                        setSelectedUpgrade(row);
                                        setRetryModalVisible(true);
                                        // A migration that pins its transaction mode forces the
                                        // toggle to that value; otherwise default to on.
                                        const useTransaction = transactionModeByName.get(row.name) ?? true;
                                        setUseTransaction(useTransaction);
                                        form.setFieldsValue({
                                            useTransaction,
                                            txTimeout: 60 * 5,
                                            txWait: 60 * 5,
                                        });
                                    }}
                                />
                            </Tooltip>
                        )}
                    </div>
                ),
            }),
        );
    }

    return columns;
}

function DbUpgrades() {
    const hasReadAccess = useAuthStore(
        (state) =>
            state.pendingOrgId === undefined &&
            state.user?.orgId === SYSTEM_ORG_IDS.SYSTEM_CONSOLE &&
            state.org?.id === SYSTEM_ORG_IDS.SYSTEM_CONSOLE &&
            hasPermission(state.user?.roles ?? [], 'admin_system:read'),
    );

    if (!hasReadAccess) {
        return (
            <PageController route={Route}>
                <div className="mx-auto max-w-5xl p-6">
                    <Alert
                        type="warning"
                        showIcon
                        message="Admin system access required"
                        description="DB upgrades are only available from the admin console to users with admin system access."
                    />
                </div>
            </PageController>
        );
    }

    return <DbUpgradesContent />;
}

function DbUpgradesContent() {
    const canManage = useAuthStore((state) => hasPermission(state.user?.roles ?? [], 'admin_system:full'));
    const { trpcClient, trpc } = useTrpc();
    const [retryModalVisible, setRetryModalVisible] = useState(false);
    const [selectedUpgrade, setSelectedUpgrade] = useState<DbUpgrade | null>(null);
    const [form] = Form.useForm<RetryParams>();
    const [useTransaction, setUseTransaction] = useState(true);

    const [queryVariables, setQueryVariables] = usePathState<QueryVariables>('queryVariables', {
        limit: DEFAULT_PAGE_SIZE,
        orderBy: { id: 'desc' },
    });

    const { data: dbUpgrades, isFetching, refetch } = trpc.admin.dbUpgrade.listDbUpgrades.useQuery(queryVariables);

    // Migrations that pin their transaction mode on the def — the runner enforces it regardless of
    // the run-level flag, so the retry toggle is locked to the declared value for these.
    const { data: transactionModes } = trpc.admin.dbUpgrade.listDbUpgradeTransactionModes.useQuery();
    const transactionModeByName = useMemo(
        () => new Map((transactionModes ?? []).map((mode) => [mode.name, mode.useTransaction] as const)),
        [transactionModes],
    );

    const canRerun = (record: DbUpgrade) => {
        return record.status === 'retry' || record.status === 'failed' || record.canRerun;
    };

    const canRunManually = (record: DbUpgrade) => {
        return record.manualOnly && (record.status === 'pending' || canRerun(record));
    };

    const handleRunManualMigration = async (record: DbUpgrade) => {
        try {
            message.loading(`Running manual migration: ${record.name}...`);
            const result = await trpcClient.admin.dbUpgrade.doDbUpgrade.mutate({
                // Honour a migration's pinned transaction mode; the server enforces it regardless,
                // but sending the right value keeps the request truthful.
                useTransaction: transactionModeByName.get(record.name) ?? true,
                txTimeout: 60 * 5 * 1000,
                txMaxWait: 60 * 5 * 1000,
                includeManualOnly: true,
                specificMigrations: [record.name],
            });
            showUpgradeResult(result, refetch);
        } catch (error) {
            message.destroy();
            errorMessage.show(error);
        }
    };

    const columns = useDbUpgradeColumns(
        canManage,
        handleRunManualMigration,
        setSelectedUpgrade,
        setRetryModalVisible,
        setUseTransaction,
        transactionModeByName,
        form,
        canRerun,
        canRunManually,
    );

    const tableProps = usePrismaTable({
        data: dbUpgrades?.rows || [],
        total: dbUpgrades?.total || 0,
        loading: isFetching,
        columns,
        rowKey: 'id',
        queryParams: queryVariables,
        onParamsChange: setQueryVariables,
        pagination: {
            position: 'both',
            showSizeChanger: true,
        },
        columnVisibility: {
            enabled: true,
            persistKey: 'db-upgrades-table-columns',
            enableReordering: true,
        },
        refreshable: {
            enabled: true,
            onRefresh: () => refetch(),
        },
    });

    // When the selected migration pins its transaction mode, the toggle is locked to that value.
    const lockedUseTransaction = selectedUpgrade ? transactionModeByName.get(selectedUpgrade.name) : undefined;
    const isUseTransactionLocked = lockedUseTransaction !== undefined;

    // If the pinned modes resolve (or change) while the retry modal is already open, sync the form
    // field and local state to the locked value. Without this, a modal opened before the metadata
    // loads keeps the default `true` even after the switch becomes disabled, so the displayed and
    // submitted value could contradict the locked setting.
    useEffect(() => {
        if (!retryModalVisible || lockedUseTransaction === undefined) return;
        setUseTransaction(lockedUseTransaction);
        form.setFieldsValue({ useTransaction: lockedUseTransaction });
    }, [retryModalVisible, lockedUseTransaction, form]);

    const handleRetry = async (values: RetryParams) => {
        if (!selectedUpgrade) return;

        try {
            message.loading('Retrying...');
            await trpcClient.admin.dbUpgrade.setDbUpgradeStatus.mutate({
                id: Number(selectedUpgrade.id),
                status: 'retry',
            });
            const result = await trpcClient.admin.dbUpgrade.doDbUpgrade.mutate({
                txMaxWait: values.txWait * 1000,
                txTimeout: values.txTimeout * 1000,
                // A pinned migration's mode is authoritative; never submit a stale toggle value.
                useTransaction: lockedUseTransaction ?? values.useTransaction,
                // Include manual migrations if retrying a manual-only migration
                includeManualOnly: selectedUpgrade.manualOnly || false,
                // Run specific migration if it's manual-only
                specificMigrations: selectedUpgrade.manualOnly ? [selectedUpgrade.name] : undefined,
            });
            showUpgradeResult(result, refetch);
            setRetryModalVisible(false);
        } catch (error) {
            message.destroy();
            errorMessage.show(error);
        }
    };

    return (
        <PageController route={Route}>
            <Alert
                type="warning"
                showIcon
                title="Long-running upgrades may outlast the request"
                description="Requests time out five minutes after receipt, including authentication and setup. An upgrade may continue and commit after a timeout, but its result summary will not be returned. Refresh this list and check the upgrade status and server logs before retrying. Increasing the transaction timeout does not extend the request deadline."
                className="mb-4"
            />
            <TableToolbar
                variant="enhanced"
                actions={
                    canManage ? (
                        <Popconfirm
                            title="Are you sure you want to run all pending upgrades?"
                            onConfirm={async () => {
                                try {
                                    message.loading('Running upgrades...');
                                    const result = await trpcClient.admin.dbUpgrade.doDbUpgrade.mutate({});
                                    showUpgradeResult(result, refetch);
                                } catch (error) {
                                    message.destroy();
                                    errorMessage.show(error);
                                }
                            }}
                        >
                            <Button type="primary" icon={<Play />}>
                                Run Upgrades
                            </Button>
                        </Popconfirm>
                    ) : undefined
                }
            />

            <PrismaTable {...tableProps} size="small" scroll={{ x: 'max-content' }} />

            <Modal
                title="Retry Upgrade Parameters"
                open={retryModalVisible}
                onCancel={() => {
                    setRetryModalVisible(false);
                    setSelectedUpgrade(null);
                    form.resetFields();
                    setUseTransaction(true);
                }}
                onOk={form.submit}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleRetry}
                    initialValues={{
                        useTransaction: true,
                        txTimeout: 60 * 5,
                        txWait: 60 * 5,
                    }}
                >
                    <Form.Item
                        name="useTransaction"
                        label="Use Transaction"
                        valuePropName="checked"
                        tooltip={
                            isUseTransactionLocked
                                ? lockedUseTransaction
                                    ? 'This migration always runs inside a transaction. The setting is fixed by the migration and cannot be changed.'
                                    : 'This migration always runs without a transaction. The setting is fixed by the migration and cannot be changed.'
                                : undefined
                        }
                    >
                        <Switch disabled={isUseTransactionLocked} onChange={(checked) => setUseTransaction(checked)} />
                    </Form.Item>
                    <Form.Item
                        name="txTimeout"
                        label="Transaction Timeout (seconds)"
                        rules={[
                            {
                                required: useTransaction,
                                message: 'Please input transaction timeout',
                            },
                        ]}
                    >
                        <InputNumber min={1} max={60 * 10} disabled={!useTransaction} />
                    </Form.Item>
                    <Form.Item
                        name="txWait"
                        label="Transaction Wait (seconds)"
                        rules={[
                            {
                                required: useTransaction,
                                message: 'Please input transaction wait time',
                            },
                        ]}
                    >
                        <InputNumber min={1} max={60 * 10} disabled={!useTransaction} />
                    </Form.Item>
                </Form>
            </Modal>
        </PageController>
    );
}
